/**
 * craft_guide.ts — the PURE rules of the Crafting Guide (2026-09-10): what a
 * player holds vs. what a schematic's slots take, slot by slot, and the
 * verdict — READY (craftable now), MISSING (short on materials) or LOCKED
 * (a complex design without its schematic). Harness-pinned; the window only
 * renders these answers.
 */
import { FAMILY_BY_KEY } from './resource_catalog.js';
import { SCHEMATIC_BY_KEY, requiresSchematic, type Schematic } from './schematic_catalog.js';

export interface Holdings {
  /** Best single stack qty per family key (a slot draws from ONE stack). */
  stackMaxByFamily: Map<string, number>;
  stackMaxByClass: Map<string, number>;
  itemCountByKey: Map<string, number>;
  known: Set<string>;
}

export interface SlotVerdict { label: string; have: number; need: number; ok: boolean; componentKey?: string }

export function slotVerdict(sch: Schematic, slotIdx: number, h: Holdings): SlotVerdict {
  const slot = sch.slots[slotIdx];
  if (slot.accepts.startsWith('component:')) {
    const key = slot.accepts.slice(10);
    // Two slots wanting the same component draw distinct rows: count the
    // rows already claimed by earlier slots of this schematic.
    let claimed = 0;
    for (let i = 0; i < slotIdx; i++) if (sch.slots[i].accepts === slot.accepts) claimed += sch.slots[i].qty;
    const have = Math.max(0, (h.itemCountByKey.get(key) ?? 0) - claimed);
    return { label: `${slot.qty} × ${SCHEMATIC_BY_KEY[key]?.name ?? key}`, have: Math.min(have, slot.qty), need: slot.qty, ok: have >= slot.qty, componentKey: key };
  }
  if (slot.accepts.startsWith('family:')) {
    const fam = slot.accepts.slice(7);
    const have = h.stackMaxByFamily.get(fam) ?? 0;
    return { label: `${slot.qty} × ${FAMILY_BY_KEY[fam]?.label ?? fam}`, have: Math.min(have, slot.qty), need: slot.qty, ok: have >= slot.qty };
  }
  const cls = slot.accepts.slice(6);
  const have = h.stackMaxByClass.get(cls) ?? 0;
  return { label: `${slot.qty} × any ${cls}`, have: Math.min(have, slot.qty), need: slot.qty, ok: have >= slot.qty };
}

export type Verdict = 'ready' | 'missing' | 'locked';

export function verdictOf(sch: Schematic, h: Holdings): { verdict: Verdict; slots: SlotVerdict[] } {
  const slots = sch.slots.map((_, i) => slotVerdict(sch, i, h));
  if (requiresSchematic(sch) && !h.known.has(sch.key)) return { verdict: 'locked', slots };
  return { verdict: slots.every((s) => s.ok) ? 'ready' : 'missing', slots };
}

